import { type ErrorComponentProps } from '@tanstack/react-router'
import { AlertTriangleIcon, RotateCcwIcon } from 'lucide-react'
import { useEffect } from 'react'

import { Button } from '@/components/ui/button'

const RELOAD_FLAG = 'oo.chunk-reload'

/**
 * Un import dynamique qui échoue (chunk absent après un déploiement, ou
 * dépendance ré-optimisée par le serveur de développement) se résout par un
 * rechargement complet. On ne le tente qu'une fois par navigation pour ne
 * jamais boucler.
 */
function isStaleChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  return /Failed to fetch dynamically imported module|Importing a module script failed|Outdated Optimize Dep/i.test(
    error.message,
  )
}

function reloadOnce(): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG) === window.location.href) return false
    sessionStorage.setItem(RELOAD_FLAG, window.location.href)
  } catch {
    return false
  }
  window.location.reload()
  return true
}

export function RouteErrorBoundary({ error, reset }: ErrorComponentProps) {
  const message = error instanceof Error ? error.message : 'Erreur inattendue'
  const stale = isStaleChunkError(error)

  useEffect(() => {
    if (stale) reloadOnce()
  }, [stale])

  return (
    <div
      role="alert"
      className="flex min-h-[60dvh] flex-col items-center justify-center gap-5 px-6 text-center"
    >
      <div className="flex size-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
        <AlertTriangleIcon className="size-6" />
      </div>
      <div className="space-y-2">
        <h1 className="text-xl font-semibold tracking-tight">Une erreur est survenue</h1>
        <p className="max-w-md text-sm break-words text-muted-foreground">{message}</p>
      </div>
      <Button variant="outline" onClick={reset}>
        <RotateCcwIcon />
        Réessayer
      </Button>
    </div>
  )
}
